import { Link } from 'react-router-dom';

export const artificialSweetenersGuide = {
  title: 'The Complete Guide to Artificial Sweeteners',
  date: '2025-01-15',
  readTime: '7 min read',
  category: 'Health',
  author: 'FoodLens Team',
  image: '/images/blog/sweeteners.svg',
  excerpt: 'Learn about artificial sweeteners, their health effects, and better alternatives. Research-backed information to help you make informed choices.',
  keywords: 'artificial sweeteners health effects, aspartame safe, sucralose gut health, are artificial sweeteners bad for you, sugar substitutes guide, types of artificial sweeteners, sweetener alternatives',
  content: () => (
    <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-a:text-brand-600 prose-strong:text-gray-900 prose-p:my-3 prose-h2:mt-6 prose-h2:mb-3 prose-ul:my-3 prose-li:my-1">
      <p>Artificial sweeteners are everywhere in our food supply, from diet sodas to "sugar-free" snacks. Understanding these compounds and their effects on health is crucial for making informed dietary choices.</p>

      <h2>Common Types of Artificial Sweeteners</h2>
      <p>The most prevalent artificial sweeteners include:</p>
      <ul>
        <li><strong><Link to="/ingredients/aspartame">Aspartame</Link>:</strong> Found in diet sodas and sugar-free gum</li>
        <li><strong><Link to="/ingredients/sucralose">Sucralose</Link> (Splenda):</strong> Heat-stable, used in baked goods</li>
        <li><strong>Saccharin:</strong> One of the oldest artificial sweeteners</li>
        <li><strong>Acesulfame Potassium (Ace-K):</strong> Often combined with other sweeteners</li>
        <li><strong>Neotame:</strong> Extremely sweet, used in small quantities</li>
      </ul>
      <p className="text-sm italic bg-brand-50 p-4 rounded-lg border-l-4 border-brand-600">
        💡 <strong>Learn More:</strong> Click any sweetener above to see detailed research, health concerns, and scientific studies about that specific ingredient.
      </p>

      <h2>Health Considerations</h2>
      <p>Research on artificial sweeteners reveals several concerns:</p>
      <ul>
        <li><strong>Gut microbiome:</strong> May alter beneficial bacteria</li>
        <li><strong>Glucose metabolism:</strong> Potential impacts on blood sugar control</li>
        <li><strong>Cravings:</strong> May increase desire for sweet foods</li>
        <li><strong>Individual sensitivity:</strong> Some people experience headaches or other symptoms</li>
      </ul>

      <h2>Natural Alternatives</h2>
      <p>Consider these natural sweetening options:</p>
      <ul>
        <li>Raw honey (in moderation)</li>
        <li>Pure maple syrup</li>
        <li>Dates and date paste</li>
        <li><Link to="/ingredients/stevia">Stevia</Link> (leaf extract)</li>
        <li>Monk fruit extract</li>
      </ul>
      <p>
        Want to explore more ingredients? Visit our <Link to="/ingredients">Ingredients Research Library</Link> to learn about hundreds of food ingredients with scientific research and citations.
      </p>

      <h2>Reading Labels with FoodLens AI</h2>
      <p>FoodLens AI identifies artificial sweeteners even when they're hidden under chemical names or listed as part of flavor compounds. Our AI recognizes hundreds of sweetener variations to help you make informed choices.</p>

      <p>Remember, the goal isn't perfection but awareness. Use FoodLens AI to understand what's in your food and make choices that align with your health goals.</p>
    </div>
  )
};
